import React, { useState, useEffect, useRef } from 'react';
import '../../styles/components/HomeSection/Screenshots.css';

const Screenshots: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  const screenshots = [
    {
      id: 1,
      title: 'Welcome to RushHour',
      description: 'Sign in quickly with Google (more features coming soon!)',
      image: require('../../assets/screenshots/RushHour_app_screenshot_1.png'),
      alt: 'RushHour App - Login'
    },
    {
      id: 2,
      title: 'Login With Google',
      description: 'Login with Google to get started',
      image: require('../../assets/screenshots/RushHour_app_screenshot_2.png'),
      alt: 'RushHour App - Login With Google'
    },
    {
      id: 3,
      title: 'Register Vehicles',
      description: 'Register vehicles, yours or others, and start tracking their blocking situation',
      image: require('../../assets/screenshots/RushHour_app_screenshot_3.png'),
      alt: 'RushHour App - Register Vehicles'
    },
    {
      id: 4,
      title: 'Car Finder Functionality',
      description: 'Find quickly any vehicles by country and license plate number',
      image: require('../../assets/screenshots/RushHour_app_screenshot_4.png'),
      alt: 'RushHour App - Car Finder Functionality'
    },
    {
      id: 5,
      title: 'Settings & Preferences',
      description: 'Manage your account and app preferences',
      image: require('../../assets/screenshots/RushHour_app_screenshot_5.png'),
      alt: 'RushHour App - Settings & Preferences'
    },
    {
      id: 6,
      title: 'Easy to Use',
      description: 'Simple and intuitive design for seamless navigation, see who\'s blocking you and who are you blocking',
      image: require('../../assets/screenshots/RushHour_app_screenshot_6.png'),
      alt: 'RushHour App - Easy to Use'
    },
    {
      id: 7,
      title: 'Notification System',
      description: 'Send instant notifications when you need to leave, and receive instant if you are blocking someone else',
      image: require('../../assets/screenshots/RushHour_app_screenshot_7.png'),
      alt: 'RushHour App - Notification & Alarm'
    }
  ];

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const nextSlide = () => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    if (isMobile) {
      setCurrentIndex((prevIndex) => 
        prevIndex === screenshots.length - 1 ? 0 : prevIndex + 1
      );
    } else {
      setCurrentIndex((prevIndex) => 
        prevIndex === screenshots.length - 3 ? 0 : prevIndex + 1
      );
    }
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    if (isMobile) {
      setCurrentIndex((prevIndex) => 
        prevIndex === 0 ? screenshots.length - 1 : prevIndex - 1
      );
    } else {
      setCurrentIndex((prevIndex) => 
        prevIndex === 0 ? screenshots.length - 3 : prevIndex - 1
      );
    }
  };

  const goToSlide = (index: number) => {
    if (isTransitioning || index === currentIndex) return;
    
    setIsTransitioning(true);
    setCurrentIndex(index);
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  // Reset transition state after animation completes
  useEffect(() => {
    if (isTransitioning) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 400);
      
      return () => clearTimeout(timer);
    }
  }, [isTransitioning]);

  // Calculate the transform for smooth sliding
  const getTransformStyle = () => {
    if (isMobile) {
      // Mobile: horizontal sliding with full width
      const slideWidth = 100; // 100% width per slide
      const offset = -currentIndex * slideWidth;
      return `translateX(${offset}%)`;
    } else {
      // Desktop: horizontal sliding
      const slideWidth = 270; // Width of each slide + gap
      const offset = -currentIndex * slideWidth;
      return `translateX(${offset}px)`;
    }
  };

  return (
    <section id="screenshots" className="screenshots-section">
      <h2 className="screenshots-title">See RushHour in Action</h2>
      <div className="screenshots-container">
        <div className="carousel-container">
          {/* Only show navigation buttons on desktop */}
          {!isMobile && (
            <>
              <button 
                className="carousel-button carousel-button-prev" 
                onClick={prevSlide}
                disabled={isTransitioning}
                aria-label="Previous screenshot"
              >
                ‹
              </button>
              
              <button 
                className="carousel-button carousel-button-next" 
                onClick={nextSlide}
                disabled={isTransitioning}
                aria-label="Next screenshot"
              >
                ›
              </button>
            </>
          )}
          
          <div 
            className="carousel-viewport"
            ref={carouselRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div 
              className={`carousel-track ${isTransitioning ? 'transitioning' : ''} ${isMobile ? 'mobile' : 'desktop'}`}
              style={{ transform: getTransformStyle() }}
            >
              {screenshots.map((screenshot, index) => (
                <div 
                  key={screenshot.id} 
                  className={`screenshot-item ${isMobile ? 'mobile-item' : 'desktop-item'}`}
                >
                  <img 
                    src={screenshot.image} 
                    alt={screenshot.alt} 
                    className="screenshot-image" 
                  />
                  <div className="screenshot-overlay">
                    <h3>{screenshot.title}</h3>
                    <p>{screenshot.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="carousel-indicators">
          {Array.from({ length: isMobile ? screenshots.length : screenshots.length - 2 }, (_, index) => (
            <button
              key={index}
              className={`indicator-dot ${currentIndex === index ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              disabled={isTransitioning}
              aria-label={`Go to screenshot ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Screenshots;
